import React, { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { isChunkLoadError, isReloadInFlight, reloadForStaleChunk } from '../../lib/staleChunk'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  context?: string
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chunk com hash antigo após deploy: um reload resolve. Segunda camada
    // além do listener 'vite:preloadError' em main.tsx; se o guard bloquear
    // (já recarregou e o erro persiste), cai no fluxo normal e vai ao Sentry.
    // `isReloadInFlight` cobre o caso em que o listener já disparou o reload
    // para este mesmo chunk: o reload é assíncrono, o React ainda lança o erro
    // no meio do caminho, e isso não é uma falha a reportar.
    if (isChunkLoadError(error) && (reloadForStaleChunk() || isReloadInFlight())) {
      return
    }

    this.setState({
      error,
      errorInfo
    })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: this.props.context ? { boundary: this.props.context } : undefined,
    })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleRetry = () => {
    // React.lazy guarda a promise rejeitada em cache: re-renderizar um chunk
    // que falhou relança o mesmo erro. Só um reload busca os chunks novos.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-cream-50 dark:bg-ink-900 flex items-center justify-center p-4">
          <div className="bg-surface dark:bg-ink-700 rounded-caderno-md border border-cream-400 dark:border-ink-500 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-blood-100 dark:bg-blood-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-blood-500" />
            </div>

            <h2 className="text-xl font-bold text-ink-900 dark:text-cream-50 mb-2">
              Ops! Algo deu errado
            </h2>

            <p className="text-ink-500 dark:text-ink-300 mb-6">
              Ocorreu um erro inesperado. Nossa equipe foi notificada e está trabalhando para resolver o problema.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-cream-100 dark:bg-ink-700 rounded-caderno-sm text-left">
                <h3 className="text-sm font-medium text-ink-900 dark:text-cream-50 mb-2">
                  Detalhes do Erro (Desenvolvimento):
                </h3>
                <pre className="text-xs text-blood-500 overflow-auto max-h-32">
                  {this.state.error.message}
                  {this.state.error.stack && (
                    <>
                      {'\n\n'}
                      {this.state.error.stack}
                    </>
                  )}
                </pre>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-terra-500 text-white rounded-caderno-sm hover:bg-terra-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Tentar Novamente</span>
              </button>

              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-ink-500 text-white rounded-caderno-sm hover:bg-ink-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Recarregar Página</span>
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary